/**
 * Pure row-level validation and auto-fix helpers for the media-unit import pipeline.
 *
 * These functions are kept in @ooh/contracts so they can be shared between:
 *  - apps/api  — dry-run validation endpoint (POST /api/import/:id/validate)
 *  - apps/worker — live import job processor
 *
 * No DB, no R2, no Redis — safe to call from any layer.
 */
import { MERGED_XLSX_TEMPLATE_TRANSLATION } from './dooh-xlsx-template-translation.js';
import { MediaTypeEnum } from '../schemas/media-unit-variants.js';
import { resolveMappedToColumn, TAG_DB_COLUMN } from '../schemas/import-job-field-options.js';
import type { ColumnMappingItem, AutofixRow } from '../schemas/import-job.js';
import {
  DECIMAL_COORDINATE_RE,
  parseCoordinateInput as parseCoordinateInputShared,
} from '../lib/parse-dms-coordinate.js';
import { applyPattern, applyValueMap } from './transforms.js';
import { normalizeMediaType } from './normalize-media-type.js';

/**
 * Strips the required-marker suffix (" *") and trims an XLSX header string.
 * Custom mappings store `sourceColumn` exactly as it appears in the file header
 * (often with a trailing " *"), but the parsed raw row keys have that suffix
 * stripped (apps/api parseAllDataRows / apps/worker parseDataRows). Normalising
 * the source column here lets the lookup succeed for both forms (CU-86ca6pvjh).
 */
function normalizeSourceColumn(header: string): string {
  return header.replace(/\s*\*\s*$/, '').trim();
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MappedRow {
  [columnName: string]: string | string[] | undefined;
  tags?: string[];
}

export interface RowError {
  field: string;
  message: string;
  code?: string;
  params?: Record<string, string>;
  severity: 'error' | 'warning';
}

export interface AutoFixResult {
  fixed: MappedRow;
  autofixes: AutofixRow[];
}

// ── applyColumnMapping ────────────────────────────────────────────────────────

/**
 * Maps raw XLSX row object (keyed by Polish header string) to DB column names
 * using MERGED_XLSX_TEMPLATE_TRANSLATION (OOH + DOOH, merged) — both template
 * types set `mappingConfirmed = true` at upload and fall back to this
 * function, so DOOH-only headers must resolve here too, not just OOH ones.
 *
 * When multiple headers map to the same column (e.g. notes), last non-empty value wins.
 * Exception: columns mapped to { table: 'media_unit_tags', column: 'tag' } are
 * accumulated into a `tags: string[]` array, preserving template column order.
 */
function accumulateTagValue(result: MappedRow, rawValue: string | undefined): void {
  if (rawValue === undefined) return;
  const trimmed = rawValue.trim();
  const existing = (result['tags'] as string[] | undefined) ?? [];
  result['tags'] = trimmed !== '' ? [...existing, trimmed] : existing;
}

export function applyColumnMapping(rawRow: Record<string, string>): MappedRow {
  const result: MappedRow = {};
  for (const [header, entry] of Object.entries(MERGED_XLSX_TEMPLATE_TRANSLATION)) {
    const value = rawRow[header];
    if (entry.table === 'media_unit_tags' && entry.column === 'tag') {
      // Accumulate tag values; only process if the raw row contains this tag column header
      accumulateTagValue(result, value);
    } else if (value !== undefined && value !== '') {
      result[entry.column] = value;
    }
  }
  return result;
}

// ── buildCustomMappedRow ──────────────────────────────────────────────────────

/**
 * Builds a MappedRow from a raw XLSX row using a custom column mapping.
 * - For each mapping entry whose mappedTo is non-null, reads the raw value
 *   (trying both the raw and " *"-stripped source-column form) and writes it to
 *   the DB column key (translating FIELD_OPTIONS keys via resolveMappedToColumn).
 * - Entries with mappedTo === null are skipped entirely.
 * - Entries carrying a `transform` (pattern/constant/valueMap) are routed
 *   through applyTransformEntry instead of the plain 1:1 write — see
 *   import-mapping-transforms/spec.md "Transform descriptor on column mappings".
 * - Tag targets (resolved column === 'tag') accumulate into a `tags: string[]`,
 *   mirroring applyColumnMapping so both mapping paths agree on shape.
 * - Source columns absent from the raw row are not written to the result.
 *
 * The DB-column translation and source-column normalisation keep the custom
 * mapping path aligned with both validateRow and the worker upsert, which read
 * DB column keys (media_type, name, code, grw_id, …) — see CU-86ca6pvjh.
 *
 * @param warnings — output array (pushed to in place) for per-row transform
 *   warnings/errors: a non-matching pattern pushes a 'warning', an unmapped
 *   valueMap value pushes an 'error'. Optional — callers that don't pass one
 *   simply don't collect these (existing 2-arg call sites are unaffected).
 */
export function buildCustomMappedRow(
  rawRow: Record<string, string>,
  mapping: ColumnMappingItem[],
  warnings: RowError[] = [],
): MappedRow {
  const result: MappedRow = {};
  for (const entry of mapping) {
    if (entry.transform) {
      applyTransformEntry(result, rawRow, entry, warnings);
      continue;
    }
    if (entry.mappedTo === null) continue; // skip unmapped columns
    const value = readRawValue(rawRow, entry.sourceColumn);
    if (value === undefined) continue;
    const column = resolveMappedToColumn(entry.mappedTo);
    if (column === TAG_DB_COLUMN) {
      accumulateTagValue(result, value);
    } else {
      result[column] = value;
    }
  }
  return result;
}

/** Writes a single resolved value to the result row, respecting the tag-accumulation target. */
function writeResolvedValue(result: MappedRow, mappedTo: string, rawValue: string): void {
  const column = resolveMappedToColumn(mappedTo);
  if (column === TAG_DB_COLUMN) {
    accumulateTagValue(result, rawValue);
  } else {
    result[column] = rawValue;
  }
}

/**
 * Applies a single mapping entry's transform (pattern/constant/valueMap) to
 * the result row, pushing a warning/error to `warnings` when a pattern fails
 * to match or a valueMap value has no dictionary entry.
 */
function applyTransformEntry(
  result: MappedRow,
  rawRow: Record<string, string>,
  entry: ColumnMappingItem,
  warnings: RowError[],
): void {
  const transform = entry.transform;
  if (!transform) return;

  if (transform.type === 'constant') {
    if (entry.mappedTo === null) return;
    writeResolvedValue(result, entry.mappedTo, transform.value);
    return;
  }

  const raw = readRawValue(rawRow, entry.sourceColumn);
  if (raw === undefined || raw.trim() === '') return;

  if (transform.type === 'valueMap') {
    if (entry.mappedTo === null) return;
    const mapped = applyValueMap(raw, transform);
    if (mapped.matched) {
      writeResolvedValue(result, entry.mappedTo, mapped.value);
      return;
    }
    const column = resolveMappedToColumn(entry.mappedTo);
    warnings.push({
      field: column,
      message: `Unmapped value: '${raw.trim()}'`,
      code: 'unmapped_value',
      params: { field: column, value: raw.trim() },
      severity: 'error',
    });
    return;
  }

  // transform.type === 'pattern'
  const matchResult = applyPattern(raw, transform);
  if (!matchResult.matched) {
    warnings.push({
      field: entry.sourceColumn,
      message: `Value does not match pattern: '${raw.trim()}'`,
      code: 'pattern_no_match',
      params: { field: entry.sourceColumn, value: raw.trim(), pattern: transform.pattern },
      severity: 'warning',
    });
    return;
  }
  transform.targets.forEach((target, i) => {
    if (target === null) return;
    const captured = matchResult.valuesByPlaceholder.get(i + 1);
    if (captured === undefined) return;
    writeResolvedValue(result, target, captured);
  });
}

/** Reads rawRow[sourceColumn], falling back to the " *"-stripped header form. */
function readRawValue(rawRow: Record<string, string>, sourceColumn: string): string | undefined {
  if (sourceColumn in rawRow) return rawRow[sourceColumn];
  const normalized = normalizeSourceColumn(sourceColumn);
  return normalized in rawRow ? rawRow[normalized] : undefined;
}

// ── applyRowMapping ───────────────────────────────────────────────────────────

/**
 * Applies either a custom column mapping or the standard XLSX template translation.
 * Pass `null` as mapping to use the standard template.
 *
 * @param warnings — forwarded to buildCustomMappedRow; ignored for the standard
 *   template path (applyColumnMapping has no transforms to warn about).
 */
export function applyRowMapping(
  rawRow: Record<string, string>,
  mapping: ColumnMappingItem[] | null,
  warnings: RowError[] = [],
): MappedRow {
  return mapping ? buildCustomMappedRow(rawRow, mapping, warnings) : applyColumnMapping(rawRow);
}

// ── validateRow ───────────────────────────────────────────────────────────────

type ScalarField = string | undefined;

export function toScalarValue(value: string | string[] | undefined): ScalarField {
  if (Array.isArray(value)) return undefined;
  return value;
}

function validateRequiredString(
  errors: RowError[],
  value: string | string[] | undefined,
  field: string,
  label: string,
): void {
  const scalar = toScalarValue(value);
  if (!scalar || scalar.trim() === '') {
    errors.push({
      field,
      message: `${label} is required`,
      code: 'field_required',
      params: { field: label },
      severity: 'error',
    });
  }
}

/**
 * Validates `value` against `allowed`, case- and whitespace-insensitively,
 * using the shared `normalizeMediaType()` helper. CU-86caq3t0r: a row that
 * `getAdapter()` already resolved (e.g. 'Wielki format', 'Mural') must not be
 * rejected again here.
 */
function validateEnum(
  errors: RowError[],
  value: string | string[] | undefined,
  field: string,
  allowed: string[],
): void {
  const scalar = toScalarValue(value);
  if (!scalar || scalar.trim() === '') return;
  if (!allowed.some((option) => normalizeMediaType(option) === normalizeMediaType(scalar))) {
    errors.push({
      field,
      message: `Unknown ${field} value: '${scalar}'`,
      code: 'unknown_field_value',
      params: { field, value: scalar },
      severity: 'error',
    });
  }
}

function validateDecimalRange(
  errors: RowError[],
  value: string | string[] | undefined,
  field: string,
  min: number,
  max: number,
): void {
  const scalar = toScalarValue(value);
  if (!scalar || scalar === '') return;
  // Strict decimal-format check instead of parseFloat: parseFloat("51.234.567")
  // truncates at the second dot and returns 51.234, silently accepting an
  // ambiguous comma/dot-mixed value that should be rejected instead.
  const num = DECIMAL_COORDINATE_RE.test(scalar) ? Number(scalar) : NaN;
  if (isNaN(num) || num < min || num > max) {
    errors.push({
      field,
      message: `${field.charAt(0).toUpperCase() + field.slice(1)} must be between ${min} and ${max}`,
      code: 'field_out_of_range',
      params: { field, min: String(min), max: String(max) },
      severity: 'error',
    });
  }
}

/**
 * Validates a mapped row (keyed by DB column names). Returns array of errors (empty = valid).
 * Required fields: media_type, name (supplier), code (format), city, address, plus an
 * identity rule that requires either grw_id or supplier_number (REQ-2.5, REQ-2.6).
 *
 * @param row — MappedRow keyed by DB column names.
 * @param overrideActive — When true, skips the media_type required-field check
 *   (the worker pins the adapter via import_jobs.media_type_override, so the
 *   per-row media_type column is not required). The enum-validity check still
 *   applies when media_type is present.
 */
export function validateRow(row: MappedRow, overrideActive = false): RowError[] {
  const errors: RowError[] = [];
  // grw_id is auto-allocated only when blank AND a supplier_number is present.
  // A row with BOTH grw_id and supplier_number blank has no stable identity and
  // is rejected (REQ-2.5, REQ-2.6). Only validate grw_id length when a value is present.
  const grwId = toScalarValue(row.grw_id);
  const supplierNumber = toScalarValue(row.supplier_number);
  const grwBlank = !grwId || grwId.trim() === '';
  const supplierBlank = !supplierNumber || supplierNumber.trim() === '';
  if (grwBlank && supplierBlank) {
    errors.push({
      field: 'grw_id',
      message: 'An internal number or supplier number is required',
      code: 'grw_id_or_supplier_number_required',
      severity: 'error',
    });
  }
  if (grwId && grwId.length > 64) {
    errors.push({
      field: 'grw_id',
      message: 'grw_id exceeds 64 characters',
      code: 'field_too_long',
      params: { field: 'grw_id', max: '64' },
      severity: 'error',
    });
  }
  if (!overrideActive) {
    validateRequiredString(errors, row.media_type, 'media_type', 'media_type');
  }
  validateEnum(errors, row.media_type, 'media_type', [...MediaTypeEnum.options]);
  // supplier name maps to column 'name' in suppliers table
  validateRequiredString(errors, row.name, 'name', 'supplier');
  // format code maps to column 'code' in media_formats table
  validateRequiredString(errors, row.code, 'code', 'format');
  validateRequiredString(errors, row.city, 'city', 'city');
  validateRequiredString(errors, row.address, 'address', 'address');
  validateDecimalRange(errors, row.latitude, 'latitude', 49, 55);
  validateDecimalRange(errors, row.longitude, 'longitude', 14, 25);
  return errors;
}

// ── autoFixRow ────────────────────────────────────────────────────────────────

const DECIMAL_FIELDS = [
  'latitude',
  'longitude',
  'width_m',
  'height_m',
  'rent',
  'exposure_cost',
  'catalog_price',
  'discounted_price',
  'last_minute_price',
  'starter_price',
  'production_cost',
  'installation_cost',
  'dismantling_cost',
  'additional_costs',
  'client_production',
  'client_installation',
  'client_dismantling',
  'client_additional_fees',
  'mural_eco_production',
];

const COORDINATE_FIELDS = new Set(['latitude', 'longitude']);

/** DECIMAL_FIELDS entries that may carry a unit/currency suffix (everything except coordinates). */
const NUMERIC_UNIT_FIELDS = new Set(
  DECIMAL_FIELDS.filter((field) => !COORDINATE_FIELDS.has(field)),
);

/** Matches a trailing currency (zł/PLN) or metric-area (m²/m2) unit suffix. */
const UNIT_SUFFIX_RE = /\s*(z[łl]|pln|m²|m2)\s*$/i;

/**
 * Strips a trailing unit/currency suffix and thousands-separator whitespace
 * (e.g. "1 250,00 zł" -> "1250,00", "50 m²" -> "50") from a numeric-looking
 * value, then normalises a comma decimal separator to a dot.
 *
 * Returns null when nothing beyond a bare comma-decimal needed changing (the
 * existing decimal_comma_autofix step already covers that case, keeping its
 * code/i18n key stable for the common case) or when the result doesn't look
 * like a number — extends the existing decimal-comma auto-fix per
 * import-mapping-transforms/spec.md "Number normalization".
 */
function stripUnitSuffix(value: string): string | null {
  const withoutUnit = value.replace(UNIT_SUFFIX_RE, '').trim();
  const withoutSeparators = withoutUnit.replace(/(?<=\d)\s+(?=\d)/g, '');
  if (withoutSeparators === value) return null;
  if (!/^-?\d+(?:[.,]\d+)?$/.test(withoutSeparators)) return null;
  return withoutSeparators.replace(',', '.');
}

function buildUnitSuffixAutofix(key: string, value: string, normalized: string): AutofixRow {
  return {
    row: 0,
    field: key,
    message: 'Unit or currency suffix stripped and value normalised',
    code: 'unit_suffix_autofix',
    params: {},
    severity: 'autofix',
    originalValue: value,
    fixedValue: normalized,
  };
}

function buildDecimalCommaAutofix(key: string, value: string, newValue: string): AutofixRow {
  return {
    row: 0,
    field: key,
    message: 'Decimal comma replaced with dot',
    code: 'decimal_comma_autofix',
    params: {},
    severity: 'autofix',
    originalValue: value,
    fixedValue: newValue,
  };
}

/**
 * Applies decimal-field number normalization (unit-suffix stripping and/or
 * comma-to-dot) for one row entry, pushing any resulting autofix. Extracted
 * from autoFixRow's loop body to keep its cyclomatic complexity in check.
 */
function applyDecimalAutofix(
  key: string,
  originalValue: string,
  current: string,
  autofixes: AutofixRow[],
): string {
  if (NUMERIC_UNIT_FIELDS.has(key)) {
    const unitNormalized = stripUnitSuffix(current);
    if (unitNormalized !== null) {
      autofixes.push(buildUnitSuffixAutofix(key, originalValue, unitNormalized));
      return unitNormalized;
    }
  } else if (!DECIMAL_FIELDS.includes(key)) {
    return current;
  }
  // Replacing only the first comma can leave a second separator behind
  // (e.g. "51,234.567" -> "51.234.567"); only claim the autofix when the
  // result is actually a clean decimal number.
  const commaFixed = current.includes(',') ? current.replace(',', '.') : null;
  if (commaFixed !== null && DECIMAL_COORDINATE_RE.test(commaFixed)) {
    autofixes.push(buildDecimalCommaAutofix(key, originalValue, commaFixed));
    return commaFixed;
  }
  return current;
}

// Local alias so the internal autoFixRow call below keeps working.
const parseCoordinateInput = parseCoordinateInputShared;

// Re-export from shared lib so existing consumers (`import { parseCoordinateInput } from
// '@ooh/contracts'`) keep working without changes.
export { parseCoordinateInputShared as parseCoordinateInput };

function buildCoordinateAutofix(
  key: string,
  value: string,
  coordinate: string,
  wasDecimal: boolean,
): AutofixRow {
  return {
    row: 0,
    field: key,
    message: wasDecimal ? 'Coordinate precision normalised' : 'DMS coordinate converted to decimal',
    code: wasDecimal ? 'coordinate_precision_autofix' : 'dms_coordinate_autofix',
    params: {},
    severity: 'autofix',
    originalValue: value,
    fixedValue: coordinate,
  };
}

/** Applies auto-fix rules: decimal comma->dot, whitespace strip. Arrays are passed through unchanged. */
export function autoFixRow(row: MappedRow): AutoFixResult {
  const fixed: MappedRow = {};
  const autofixes: AutofixRow[] = [];

  for (const [key, value] of Object.entries(row)) {
    if (value === undefined) {
      fixed[key] = value;
      continue;
    }
    // Arrays (e.g. tags: string[]) pass through unchanged — no decimal-comma fix needed
    if (Array.isArray(value)) {
      fixed[key] = value;
      continue;
    }
    let current = applyDecimalAutofix(key, value, value.trim(), autofixes);
    if (COORDINATE_FIELDS.has(key)) {
      const coordinate = parseCoordinateInput(current);
      if (coordinate !== null && coordinate !== current) {
        autofixes.push(
          buildCoordinateAutofix(key, value, coordinate, DECIMAL_COORDINATE_RE.test(current)),
        );
        current = coordinate;
      }
    }
    fixed[key] = current;
  }
  return { fixed, autofixes };
}

// ── Tag resolution ────────────────────────────────────────────────────────────

/**
 * Normalises a raw tag code for case-insensitive lookup.
 * Shared by both apps/api (dry-run validate) and apps/worker (live import).
 */
export function normaliseTagCode(code: string): string {
  return code.trim().toLowerCase();
}

/**
 * Builds the tag lookup map from a list of tag definition rows.
 * Shared by both apps/api (dry-run validate) and apps/worker (live import)
 * so both use identical normalisation and therefore agree on which codes are valid.
 */
export function buildTagMap(rows: Array<{ code: string; id: number }>): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    map.set(normaliseTagCode(row.code), row.id);
  }
  return map;
}

/**
 * Return type for resolveTagCodes — discriminated union for safe error handling.
 * On success: tagIds resolved, errors=null.
 * On failure: tagIds=null, errors contains one entry per unknown code.
 */
export type ResolveTagCodesResult =
  | { tagIds: number[]; errors: null }
  | { tagIds: null; errors: RowError[] };

/**
 * Resolves tag codes to tag_definition IDs using a preloaded lookup map.
 * - Normalises each code via normaliseTagCode() for case-insensitive matching.
 * - Deduplicates normalised codes before resolving.
 * - Empty/whitespace-only codes are skipped silently.
 * - Returns { tagIds, errors: null } on success.
 * - Returns { tagIds: null, errors } if ANY code is unknown/inactive.
 *
 * Shared by apps/api (dry-run validate) and apps/worker (live import) so
 * validation and import agree on which tag codes are valid.
 *
 * Traceability: REQ-2.21, REQ-2.22
 */
export function resolveTagCodes(
  tagMap: Map<string, number>,
  rawCodes: string[],
): ResolveTagCodesResult {
  const seen = new Set<string>();
  const tagIds: number[] = [];
  const errors: RowError[] = [];

  for (const raw of rawCodes) {
    const trimmed = raw.trim();
    const normalized = normaliseTagCode(raw);

    if (normalized === '') continue;
    if (seen.has(normalized)) continue;
    seen.add(normalized);

    const id = tagMap.get(normalized);
    if (id == null) {
      errors.push({
        field: 'tags',
        message: `unknown_tag_code: "${trimmed}"`,
        code: 'unknown_tag_code',
        params: { value: trimmed },
        severity: 'error',
      });
    } else {
      tagIds.push(id);
    }
  }

  if (errors.length > 0) return { tagIds: null, errors };
  return { tagIds, errors: null };
}
